// Phase 2 · 項目の意味（説明文）— 管理者が所有する表示専用の参照テキスト。
// 抽出・信頼度・確認状態・検索事実には一切影響しない。
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CircleNotch, PencilSimple, ArrowsClockwise as Reset, X, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const LANGS = [['ja', '日本語'], ['en', 'English']];

const EditDialog = ({ row, profileKey, language, onClose, onSaved }) => {
  const [text, setText] = useState(row.meaning || '');
  const [scope, setScope] = useState('profile');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/doc-ai/field-meanings/${row.key}`, {
        profile_key: scope === 'profile' ? profileKey : null,
        language, meaning: text,
        expected_version: scope === 'profile' && row.is_override ? row.version : undefined,
      }, { headers: authHeader() });
      toast.success('説明文を保存しました');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="meaning-edit-dialog" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <p className="text-[13px] font-semibold text-zinc-900">{row.label_ja}</p>
            <p className="font-mono text-[10.5px] text-zinc-500">{row.key}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="閉じる" title="閉じる"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100"
            data-testid="meaning-edit-close"><X size={14} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">適用範囲</label>
            <div className="mt-1 flex gap-3 text-[12px]">
              <label className="flex min-h-[32px] cursor-pointer items-center gap-1.5">
                <input type="radio" checked={scope === 'profile'} onChange={() => setScope('profile')}
                  className="accent-teal-700" data-testid="meaning-scope-profile" />図面種類別
              </label>
              <label className="flex min-h-[32px] cursor-pointer items-center gap-1.5">
                <input type="radio" checked={scope === 'global'} onChange={() => setScope('global')}
                  className="accent-teal-700" data-testid="meaning-scope-global" />共通
              </label>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">説明文（200文字まで・平文のみ）</label>
            <textarea value={text} maxLength={200} rows={3}
              onChange={(e) => setText(e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-[13px]"
              data-testid="meaning-edit-input" />
            <p className="mt-0.5 text-[10.5px] text-zinc-500">
              利用者に見せる説明文です。読み取り方法や判定には影響しません。
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}
            data-testid="meaning-edit-cancel">キャンセル</Button>
          <Button onClick={save} disabled={busy || !text.trim()}
            className="bg-teal-700 hover:bg-teal-800" data-testid="meaning-edit-save">
            {busy ? <CircleNotch size={11} className="mr-1 animate-spin" /> : <Check size={11} className="mr-1" />}
            保存
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function FieldMeaningsSection({ profiles }) {
  const drawing = (profiles || []).filter((p) => p.is_drawing);
  const [profileKey, setProfileKey] = useState('');
  const [language, setLanguage] = useState('ja');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!profileKey && drawing.length) setProfileKey(drawing[0].key);
  }, [drawing, profileKey]);

  const load = async (pk, lang) => {
    if (!pk) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/doc-ai/field-meanings`,
        { params: { profile_key: pk, language: lang }, headers: authHeader() });
      const profile = (profiles || []).find((p) => p.key === pk) || {};
      const labels = {};
      (profile.fields || []).forEach((f) => { labels[f.key] = f.label_ja; });
      labels.document_subtype = '図面種類';
      const forbidden = new Set(profile.forbidden_fields || []);
      if ((profile.allowed_fields || []).includes('material_raw')
          && !forbidden.has('material_family')) {
        labels.material_family = '材質分類';
      }
      const meanings = r.data?.meanings || {};
      setRows(Object.keys(labels)
        .filter((k) => k !== 'title')
        .map((k) => ({ key: k, label_ja: labels[k], ...(meanings[k] || {}) })));
    } catch (e) {
      toast.error('説明文の取得に失敗しました');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(profileKey, language); }, [profileKey, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const doReset = async (row) => {
    if (!window.confirm(`${row.label_ja} の管理者設定を削除して、既定の説明文へ戻します。`)) return;
    try {
      await axios.delete(`${API}/api/doc-ai/field-meanings/${row.key}`, {
        params: { profile_key: row.scope === '*' ? undefined : profileKey, language },
        headers: authHeader(),
      });
      toast.success('既定に戻しました');
      await load(profileKey, language);
    } catch (e) { toast.error(e?.response?.data?.detail || 'リセットに失敗しました'); }
  };

  if (!drawing.length) return null;
  return (
    <div className="space-y-2" data-testid="field-meanings-section">
      <h2 className="text-[13px] font-semibold text-zinc-700">項目の意味（説明文）</h2>
      <p className="text-[11.5px] text-zinc-500">
        レビュー画面で各項目の下に出る説明文です。読み取りの動作・信頼度・確認状態は変わりません。
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={profileKey} onChange={(e) => setProfileKey(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 px-2 text-[12px]"
          aria-label="図面の種類" data-testid="meaning-profile-select">
          {drawing.map((p) => <option key={p.key} value={p.key}>{p.label_ja}</option>)}
        </select>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 px-2 text-[12px]"
          aria-label="言語" data-testid="meaning-language-select">
          {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {rows.length > 0 && (
          <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-600"
            data-testid="meaning-coverage">
            説明文: {rows.filter((r) => r.meaning).length}/{rows.length} 項目
          </span>
        )}
        {loading && <CircleNotch size={14} className="animate-spin text-zinc-400" />}
      </div>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.key} className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2"
            data-testid={`meaning-row-${row.key}`}>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-medium text-zinc-900">
                {row.label_ja}
                {row.is_override && (
                  <span className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-amber-800">
                    管理者上書き
                  </span>
                )}
              </p>
              <p className="font-mono text-[10px] text-zinc-400">{row.key}</p>
              <p className="pt-0.5 text-[12px] text-zinc-700" data-testid={`meaning-text-${row.key}`}>
                {row.meaning || '（説明文なし）'}
              </p>
              <p className="pt-0.5 text-[10.5px] text-zinc-400">
                由来: {row.source_label_ja || '—'}
                {row.updated_by && ` · 更新: ${row.updated_by}`}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button size="sm" variant="outline" className="h-8 text-[11px]"
                onClick={() => setEditing(row)} data-testid={`meaning-edit-${row.key}`}>
                <PencilSimple size={11} className="mr-1" /> 編集
              </Button>
              {row.is_override && (
                <Button size="sm" variant="outline"
                  className="h-8 border-amber-300 text-[11px] text-amber-800 hover:bg-amber-50"
                  onClick={() => doReset(row)} data-testid={`meaning-reset-${row.key}`}>
                  <Reset size={11} className="mr-1" /> 既定に戻す
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <EditDialog row={editing} profileKey={profileKey} language={language}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(profileKey, language); }} />
      )}
    </div>
  );
}
